import { useState, useCallback, useRef } from 'react';
import { chatService, StreamChunk } from '../services/chatService';
import toast from 'react-hot-toast';

export interface UsageStats {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost?: number;
}

export const useAIChat = (conversationId: string) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Send message and get standard (non-streaming) response
   */
  const sendMessage = useCallback(
    async (content: string) => {
      try {
        const response = await chatService.sendMessage(conversationId, content);

        setUsage({
          promptTokens: response.usage.promptTokens,
          completionTokens: response.usage.completionTokens,
          totalTokens: response.usage.totalTokens,
        });

        return response;
      } catch (error: any) {
        console.error('Failed to send message:', error);

        if (error.response?.status === 503) {
          setIsAvailable(false);
          toast.error('AI service is not available. Please contact support.');
        } else if (error.response?.status === 402) {
          toast.error('API quota exceeded. Please contact support.');
        } else if (error.response?.status === 429) {
          toast.error('Rate limit exceeded. Please try again in a moment.');
        } else {
          toast.error(error.response?.data?.message || 'Failed to send message');
        }

        throw error;
      }
    },
    [conversationId]
  );

  /**
   * Send message and stream response
   */
  const sendStreamingMessage = useCallback(
    async (
      content: string,
      onChunk?: (chunk: string) => void,
      onComplete?: (messageId: string, duration: number) => void
    ) => {
      setIsStreaming(true);
      setStreamingMessage('');
      abortControllerRef.current = new AbortController();

      try {
        let fullMessage = '';

        for await (const chunk of chatService.streamMessage(conversationId, content)) {
          // Check if aborted
          if (abortControllerRef.current?.signal.aborted) {
            break;
          }

          if (chunk.error) {
            toast.error(chunk.error);
            break;
          }

          if (chunk.chunk) {
            fullMessage += chunk.chunk;
            setStreamingMessage(fullMessage);
            onChunk?.(chunk.chunk);
          }

          if (chunk.done && chunk.messageId && chunk.duration !== undefined) {
            onComplete?.(chunk.messageId, chunk.duration);
            break;
          }
        }

        return fullMessage;
      } catch (error: any) {
        console.error('Streaming error:', error);

        if (error.message?.includes('not available')) {
          setIsAvailable(false);
          toast.error('AI service is not configured');
        } else {
          toast.error('Failed to stream response');
        }

        throw error;
      } finally {
        setIsStreaming(false);
        setStreamingMessage('');
      }
    },
    [conversationId]
  );

  /**
   * Cancel ongoing streaming
   */
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
      setStreamingMessage('');
    }
  }, []);

  /**
   * Check if AI chat is available
   */
  const checkAvailability = useCallback(async () => {
    try {
      const available = await chatService.checkAvailability();
      setIsAvailable(available);
      return available;
    } catch (error) {
      setIsAvailable(false);
      return false;
    }
  }, []);

  /**
   * Get usage stats for conversation
   */
  const getConversationUsage = useCallback(async () => {
    try {
      return await chatService.getConversationUsage(conversationId);
    } catch (error) {
      console.error('Failed to get usage stats:', error);
      return null;
    }
  }, [conversationId]);

  /**
   * Estimate cost for a message
   */
  const estimateCost = useCallback((text: string, model?: string) => {
    return chatService.estimateCost(text, model);
  }, []);

  return {
    // State
    isStreaming,
    streamingMessage,
    usage,
    isAvailable,

    // Actions
    sendMessage,
    sendStreamingMessage,
    cancelStreaming,
    checkAvailability,
    getConversationUsage,
    estimateCost,
  };
};
